import { useCallback, useEffect, useRef, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { subscribeToClassRidesPulled } from "../local/rideCacheEvents";

// Card #1992 — "No Show", presiding judge (judge position C) only. Modeled
// directly on useRideUtilityEvents (card #139): a No Show is a RIDE-level
// fact (score-api's ride.noShow, not one judge's own ScoreSheet), so every
// judge position scoring the SAME ride sees the SAME mark, delivered by
// the 5s change check (card #3200: one fetch on open, then the class re-pull; the 4s per-ride poll is gone). That is how a
// mark raised at C waterfalls to every other panel judge's sheet and shows
// up there as an active alert within one change-check interval (card AC8/AC9).
//
// mark()/undo() are DIRECT writes, not queued through the offline sync
// engine — same choice #916's Scratch/No Show gavel items made (see
// ScoringForm.jsx's confirmTermReason): this is a live cloud write with its
// own confirm step and loading/error state, not something the autosave
// queue should replay later out of order.

/**
 * @param {string} rideId
 * @returns {{
 *   noShow: object|null,
 *   loading: boolean,
 *   error: Error|null,
 *   mark: () => Promise<void>,
 *   undo: () => Promise<void>,
 *   marking: boolean,
 *   markError: Error|null,
 * }}
 */
// card #3196 - `online` (default true) skips the fetch while offline: these are
// live, shared ride facts with nothing local to read, so offline keeps the last
// value and makes no request; the online flip re-runs the effect and refreshes.
export function useRideNoShow(rideId, { online = true } = {}) {
  const onlineRef = useRef(online);
  onlineRef.current = online;
  const scoreApi = useScoreApi();
  const [noShow, setNoShow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState(null);

  const rideIdRef = useRef(rideId);
  rideIdRef.current = rideId;
  const apiRef = useRef(scoreApi);
  apiRef.current = scoreApi;

  // Same stale-response guard as useRideUtilityEvents: a poll tick that
  // started before a mark()/undo() lands must not clobber the fresher state
  // with its own late-arriving read.
  const refreshGenRef = useRef(0);

  const refresh = useCallback(async () => {
    const id = rideIdRef.current;
    if (!id) return;
    if (!onlineRef.current) {
      setLoading(false);
      return;
    }
    const gen = ++refreshGenRef.current;
    const isStale = () => gen !== refreshGenRef.current;
    try {
      const result = unwrap(await apiRef.current.rides.getRideNoShow(id)) ?? null;
      if (isStale()) return;
      setNoShow(result);
      setError(null);
    } catch (err) {
      if (isStale()) return;
      setError(err);
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, []);

  // card #3200 - one fetch when the sheet opens (and when connectivity
  // returns), NO interval. After that the value arrives with the class re-pull
  // the 5s change check does when any ride in this class changes: the pulled
  // ride row carries `noShow`, so no per-ride request is needed (see
  // local/rideCacheEvents.js).
  useEffect(() => {
    if (!rideId) {
      setNoShow(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    refresh();
    return undefined;
  }, [rideId, refresh, online]);

  useEffect(
    () =>
      subscribeToClassRidesPulled(({ rides: pulled }) => {
        const id = rideIdRef.current;
        if (!id) return;
        const row = pulled.find((r) => r.id === id);
        if (!row) return;
        // supersedes any fetch still in flight
        refreshGenRef.current += 1;
        setNoShow(row.noShow ?? null);
        setError(null);
        setLoading(false);
      }),
    [],
  );

  const mark = useCallback(async () => {
    const id = rideIdRef.current;
    if (!id) return;
    setMarking(true);
    setMarkError(null);
    try {
      const result = unwrap(await apiRef.current.rides.markRideNoShow(id));
      setNoShow(result);
    } catch (err) {
      setMarkError(err);
      throw err;
    } finally {
      setMarking(false);
    }
  }, []);

  const undo = useCallback(async () => {
    const id = rideIdRef.current;
    if (!id) return;
    setMarking(true);
    setMarkError(null);
    try {
      await apiRef.current.rides.undoRideNoShow(id);
      setNoShow(null);
    } catch (err) {
      setMarkError(err);
      throw err;
    } finally {
      setMarking(false);
    }
  }, []);

  return { noShow, loading, error, mark, undo, marking, markError };
}

export default useRideNoShow;
